import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Snackbar,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  TextField,
  InputAdornment,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AuthContext } from '../context/AuthContext';
import { enquiryService, authService } from '../api/axios';

import AdminUserManagement from '../admin/AdminUserManagement';
import AdminCatalogManagement from '../admin/AdminCatalogManagement';
import AdminAnalytics from '../admin/AdminAnalytics';
import AdminMetrics from '../admin/AdminMetrics';
import AdminEnquiryList from '../admin/AdminEnquiryList';
import ConversationModal from './ConversationModal';
import FilePreviewModal from '../components/FilePreviewModal';
import TotalValue from '../components/TotalValue';

function AdminDashboard() {
  const { user } = useContext(AuthContext);
  const location = useLocation();

  const [enquiries, setEnquiries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [bdmUsers, setBdmUsers] = useState([]);
  const [scmUsers, setScmUsers] = useState([]);
  const [usersError, setUsersError] = useState('');

  const [filterStatus, setFilterStatus] = useState('');
  const [filterRequestedBy, setFilterRequestedBy] = useState('');
  const [filterAssignedTo, setFilterAssignedTo] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState('');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const [selectedEnquiryForModal, setSelectedEnquiryForModal] = useState(null);
  const [selectedItemForModal, setSelectedItemForModal] = useState(null);

  const [showFilePreviewModal, setShowFilePreviewModal] = useState(false);
  const [fileToPreview, setFileToPreview] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const statusOptions = ["Pending with SCM", "Pending with BDM", "Closed Won", "Closed Lost", "On Hold", "Pending With Client"];

  const fetchUsersByRole = useCallback(async (role, setter) => {
    try {
      const response = await authService.get('/auth/users', { params: { role } });
      setter(response.data.map(u => u.username));
    } catch (err) {
      setUsersError(`Failed to load ${role.replace('_', ' ')} users.`);
    }
  }, []);

  const fetchFilteredEnquiries = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = {
        requestedBy: filterRequestedBy || undefined,
        assignedTo: filterAssignedTo || undefined,
        status: filterStatus || undefined,
        searchTerm: searchTerm.trim() || undefined, // Include search term in API params
        startDate: startDate ? startDate + 'T00:00:00' : undefined,
        endDate: endDate ? endDate + 'T23:59:59' : undefined,
        page: page - 1,
        size: 10
      };
      const response = await enquiryService.get('/enquiry', { params });
      setEnquiries(Array.isArray(response.data.content) ? response.data.content : []);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      setError('Failed to load enquiries.');
    } finally {
      setIsLoading(false);
    }
  }, [filterStatus, filterRequestedBy, filterAssignedTo, startDate, endDate, page, searchTerm]);

  const filters = useMemo(() => ({
    status: filterStatus,
    requestedBy: filterRequestedBy,
    assignedTo: filterAssignedTo,
    startDate,
    endDate,
  }), [filterStatus, filterRequestedBy, filterAssignedTo, startDate, endDate]);

  useEffect(() => {
    const fetchMetrics = async () => {
      setMetricsLoading(true);
      setMetricsError('');
      try {
        const params = {
          requestedBy: filters.requestedBy || undefined,
          assignedTo: filters.assignedTo || undefined,
          status: filters.status || undefined,
          startDate: filters.startDate ? `${filters.startDate}T00:00:00` : undefined,
          endDate: filters.endDate ? `${filters.endDate}T23:59:59` : undefined,
        };
        const response = await enquiryService.get('/enquiry/metrics', { params });
        setMetrics(response.data);
      } catch (err) {
        setMetricsError('Failed to load metrics. Please try again later.');
        console.error("Error fetching admin metrics:", err);
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [filters]);


  useEffect(() => {
    if (user) {
      fetchUsersByRole('BUSINESS_DEVELOPMENT', setBdmUsers);
      fetchUsersByRole('SUPPLY_CHAIN', setScmUsers);
    }
  }, [user, fetchUsersByRole]);

  useEffect(() => {
    if (location.pathname === '/admin-dashboard') {
        const handler = setTimeout(() => {
            fetchFilteredEnquiries();
        }, 500);
        return () => clearTimeout(handler);
    }
  }, [fetchFilteredEnquiries, location.pathname]);


  if (!user || user.role.toLowerCase() !== 'admin') {
    return <Box sx={{ p: 3, textAlign: 'center' }}><Typography variant="h4" color="error">Access Denied</Typography></Box>;
  }

  const handleClearFilters = () => {
    setStartDate(''); setEndDate(''); setFilterStatus('');
    setFilterRequestedBy(''); setFilterAssignedTo('');
    setSearchTerm(''); // Clear search term
  };

  const handleMetricClick = (filter) => setFilterStatus(filter.status || '');

  const handleOpenFilePreview = (attachment) => {
    if (!attachment || !attachment.id) return;
    enquiryService.get(`/enquiry/attachments/${attachment.id}/presigned-url`)
      .then(response => {
        setFileToPreview({ url: response.data, name: attachment.originalFileName, type: attachment.contentType });
        setShowFilePreviewModal(true);
      })
      .catch(err => setSnackbar({ open: true, message: `Failed to get file preview: ${err.response?.data?.message || 'Error'}`, severity: 'error' }));
  };

  const handleCloseFilePreview = () => {
      setShowFilePreviewModal(false);
      setFileToPreview(null);
  };

  const handleViewDetails = (enquiry, item = null) => {
    setSelectedEnquiryForModal(enquiry);
    setSelectedItemForModal(item);
    setIsConversationModalOpen(true);
  };

  const handleCloseConversationModal = () => {
    setIsConversationModalOpen(false);
    setSelectedEnquiryForModal(null);
    setSelectedItemForModal(null);
    fetchFilteredEnquiries();
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Box>
      <Routes>
        <Route path="/" element={
            <>
              {/* Header with Search */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h2" component="h2" sx={{ mr: 2, mb: { xs: 2, sm: 0 } }}>Admin Dashboard</Typography>
                  <TextField
                    label="Search Enquiries"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        endAdornment: searchTerm && (
                            <InputAdornment position="end">
                                <IconButton onClick={() => setSearchTerm('')} size="small"><CloseIcon /></IconButton>
                            </InputAdornment>
                        )
                    }}
                    sx={{ minWidth: 200 }}
                  />
              </Box>

              <Paper sx={{ p: 3, mb: 4, borderRadius: 2, boxShadow: 3 }}>
                  <Typography variant="h6" gutterBottom>Filter Enquiries</Typography>
                   <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4} md={2.5}>
                          <FormControl fullWidth size="small">
                              <InputLabel>Status</InputLabel>
                              <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                                  <MenuItem value=""><em>All</em></MenuItem>
                                  {statusOptions.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                              </Select>
                          </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4} md={2.5}>
                          <FormControl fullWidth size="small">
                              <InputLabel>Requested By (BDM)</InputLabel>
                              <Select value={filterRequestedBy} label="Requested By (BDM)" onChange={(e) => setFilterRequestedBy(e.target.value)}>
                                  <MenuItem value=""><em>All</em></MenuItem>
                                  {bdmUsers.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                              </Select>
                          </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={4} md={2.5}>
                          <FormControl fullWidth size="small">
                              <InputLabel>Assigned To (SCM)</InputLabel>
                              <Select value={filterAssignedTo} label="Assigned To (SCM)" onChange={(e) => setFilterAssignedTo(e.target.value)}>
                                  <MenuItem value=""><em>All</em></MenuItem>
                                  {scmUsers.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                              </Select>
                          </FormControl>
                      </Grid>
                       <Grid item xs={12} sm={6} md={3} container alignItems="center" spacing={1}>
                          <Grid item xs={6}><TextField label="From" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth/></Grid>
                          <Grid item xs={6}><TextField label="To" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth/></Grid>
                      </Grid>
                      <Grid item xs={12} sm={6} md={1.5}><Button variant="outlined" onClick={handleClearFilters} fullWidth>Clear</Button></Grid>
                  </Grid>
              </Paper>

              {usersError && <Alert severity="error" sx={{ mb: 3 }}>{usersError}</Alert>}

              <AdminMetrics
                metrics={metrics}
                isLoading={metricsLoading}
                error={metricsError}
                onMetricClick={handleMetricClick}
              />
              <TotalValue value={metrics?.totalValue} />

              <Divider sx={{ my: 4 }} />

              <Typography variant="h3" component="h3" sx={{ mb: 2, textAlign: 'center' }}>All Enquiries</Typography>
              <AdminEnquiryList
                  isLoading={isLoading}
                  error={error}
                  enquiries={enquiries}
                  onViewDetails={handleViewDetails}
                  onOpenFilePreview={handleOpenFilePreview}
                  refreshEnquiriesList={fetchFilteredEnquiries}
                  page={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
              />
            </>
        } />
        <Route path="users" element={<AdminUserManagement />} />
        <Route path="catalog" element={<AdminCatalogManagement />} />
        <Route path="analytics" element={<AdminAnalytics />} />
      </Routes>

      {isConversationModalOpen && (
        <ConversationModal
          enquiry={selectedEnquiryForModal}
          enquiryItem={selectedItemForModal}
          onClose={handleCloseConversationModal}
          userRole={user.role}
          onOpenFilePreview={handleOpenFilePreview}
          isEnquiryLevel={!selectedItemForModal}
        />
      )}

      {showFilePreviewModal && fileToPreview && (
        <FilePreviewModal fileUrl={fileToPreview.url} fileName={fileToPreview.name} fileContentType={fileToPreview.type} onClose={handleCloseFilePreview}/>
      )}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default AdminDashboard;